import { Controller, useForm } from "react-hook-form";
import _ from "@lodash";
import TextField from "@mui/material/TextField";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import React, { useState } from "react";
import Box from "@mui/material/Box";
import SearchIcon from "@mui/icons-material/Search";
import BackIcon from "@mui/icons-material/Backspace";
import {
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Card,
  MenuItem,
  SelectChangeEvent,
  Grid,
} from "@mui/material";

interface FilterProps {
  onFilterChange: (filters: {
    week: string | null;
    worksite: string | null;
    status: string | null;
    program: string | null;
    name: string | null;
    id: string | null;
  }) => void;
  availableWeeks?: string[]; // Add support for dynamic weeks
}

const defaultValues = { name: "", id: "" };
const schema = yup.object().shape({
  name: yup.string(),
  id: yup.string(),
});

function TimeSheetFilter({ onFilterChange, availableWeeks = [] }: FilterProps) {
  const [week, setWeek] = useState<string | null>(null);
  const [worksite, setWorksite] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [program, setProgram] = useState<string | null>(null);
  const { control, formState, getValues, reset } = useForm({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(schema),
  });
  const { errors } = formState;

  // Handle filter changes
  const handleWeekChange = (event: SelectChangeEvent) => {
    setWeek(event.target.value);
  };

  const handleWorksiteChange = (event: SelectChangeEvent) => {
    setWorksite(event.target.value);
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    setStatus(event.target.value);
  };

  const handleProgramChange = (event: SelectChangeEvent) => {
    setProgram(event.target.value);
  };

  // Apply filters
  const applyFilters = () => {
    const formValues = getValues();
    onFilterChange({
      week,
      worksite,
      status,
      program,
      name: formValues.name || null,
      id: formValues.id || null,
    });
  };

  // Reset all filters
  const resetFilters = () => {
    setWeek(null);
    setWorksite(null);
    setStatus(null);
    setProgram(null);
    reset(defaultValues);
    onFilterChange({
      week: null,
      worksite: null,
      status: null,
      program: null,
      name: null,
      id: null,
    });
  };

  return (
    <Card variant="outlined" className="m-24 mb-0">
      <Grid container spacing={2} alignItems="center" className="p-16">
        {/* Program Filter */}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="program-select-label">Program</InputLabel>
            <Select
              labelId="program-select-label"
              id="program-select"
              value={program || ""}
              label="Program"
              onChange={handleProgramChange}
            >
              <MenuItem value="">
                <em>All Programs</em>
              </MenuItem>
              <MenuItem value={"Summer Youth Employment"}>
                Summer Youth Employment
              </MenuItem>
              <MenuItem value={"Year-Round Youth Employment"}>
                Year-Round Youth Employment
              </MenuItem>
              <MenuItem value={"Youth Apprenticeship"}>
                Youth Apprenticeship
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Week Filter */}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="week-select-label">Week</InputLabel>
            <Select
              labelId="week-select-label"
              id="week-select"
              value={week || ""}
              label="Week"
              onChange={handleWeekChange}
            >
              <MenuItem value="">
                <em>All Weeks</em>
              </MenuItem>
              {availableWeeks && availableWeeks.length > 0 ? (
                availableWeeks.map((weekOption) => (
                  <MenuItem key={weekOption} value={weekOption}>
                    {weekOption}
                  </MenuItem>
                ))
              ) : (
                <>
                  <MenuItem value={"Week 1 (7/3/2023 – 7/7/2023)"}>
                    Week 1 (7/3/2023 – 7/7/2023)
                  </MenuItem>
                  <MenuItem value={"Week 2 (7/10/2023 – 7/14/2023)"}>
                    Week 2 (7/10/2023 – 7/14/2023)
                  </MenuItem>
                  <MenuItem value={"Week 3 (7/17/2023 – 7/21/2023)"}>
                    Week 3 (7/17/2023 – 7/21/2023)
                  </MenuItem>
                </>
              )}
            </Select>
          </FormControl>
        </Grid>

        {/* Worksite Filter */}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="worksite-select-label">Worksite</InputLabel>
            <Select
              labelId="worksite-select-label"
              id="worksite-select"
              value={worksite || ""}
              label="Worksite"
              onChange={handleWorksiteChange}
            >
              <MenuItem value="">
                <em>All Worksites</em>
              </MenuItem>
              <MenuItem value={"Head Office"}>Head Office</MenuItem>
              <MenuItem value={"Finance Division"}>Finance Division</MenuItem>
              <MenuItem value={"Marketing Division"}>
                Marketing Division
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Status Filter */}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="status-select-label">Status</InputLabel>
            <Select
              labelId="status-select-label"
              id="status-select"
              value={status || ""}
              label="Status"
              onChange={handleStatusChange}
            >
              <MenuItem value="">
                <em>All Statuses</em>
              </MenuItem>
              <MenuItem value={"App. Pending"}>Approval Pending</MenuItem>
              <MenuItem value={"Approved"}>Approved</MenuItem>
              <MenuItem value={"Rejected"}>Rejected</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Name Filter */}
        <Grid item xs={12} sm={6} md={1.5}>
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <TextField
                {...field}
                className="w-full"
                label="Name"
                placeholder="Name"
                variant="outlined"
                fullWidth
                size="small"
                error={!!errors.name}
                helperText={errors?.name?.message}
              />
            )}
          />
        </Grid>

        {/* ID Filter */}
        <Grid item xs={12} sm={6} md={1.5}>
          <Controller
            control={control}
            name="id"
            render={({ field }) => (
              <TextField
                {...field}
                className="w-full"
                label="ID"
                placeholder="ID"
                variant="outlined"
                fullWidth
                size="small"
                error={!!errors.id}
                helperText={errors?.id?.message}
              />
            )}
          />
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12} sm={6} md={1} className="flex justify-center">
          <div className="flex">
            <IconButton
              color="primary"
              aria-label="search"
              onClick={applyFilters}
            >
              <SearchIcon />
            </IconButton>
            <IconButton
              color="primary"
              aria-label="reset"
              onClick={resetFilters}
            >
              <BackIcon />
            </IconButton>
          </div>
        </Grid>
      </Grid>
    </Card>
  );
}

export default TimeSheetFilter;
